import { Alert, Box, Paper, Skeleton, Typography } from '@mui/material';
import type { EcosystemKPIs as EcosystemKPIsType } from '../../types/agents';

const WARNING_MESSAGES: Record<string, string> = {
  github:
    'GitHub data unavailable — issues and PR counts will show as 0. Check the backend logs for details.',
  emails:
    'Recent-emails source unavailable — count will show as 0. Check the backend logs for details.',
};

interface EcosystemKPIsProps {
  overview: EcosystemKPIsType | null;
  loading: boolean;
}

function formatLastEdit(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const days = Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  return date.toLocaleDateString();
}

interface KpiCellProps {
  label: string;
  value: string | number;
  hint?: string;
  alert?: boolean;
  loading?: boolean;
}

function KpiCell({ label, value, hint, alert, loading }: KpiCellProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 160,
        flex: 1,
        borderColor: alert ? 'warning.main' : 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {loading ? (
        <Skeleton width="50%" height={32} />
      ) : (
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      )}
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

export function EcosystemKPIs({ overview, loading }: EcosystemKPIsProps) {
  const cap = overview?.open_issues_global_cap ?? 15;
  const total = overview?.open_issues_total ?? 0;
  const overCap = total >= cap;
  const warnings = overview?.data_source_warnings ?? [];

  return (
    <>
      {warnings.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {warnings.map((source) => (
            <Alert key={source} severity="warning" data-testid={`agents-warning-${source}`}>
              {WARNING_MESSAGES[source] ?? `Data source "${source}" is degraded.`}
            </Alert>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <KpiCell
          label="Open agent issues"
          value={loading ? '…' : `${total} / ${cap}`}
          hint={overCap ? 'global throttle engaged' : 'global cap room'}
          alert={overCap}
          loading={loading}
        />
        <KpiCell
          label="Agents fired today"
          value={
            loading
              ? '…'
              : `${overview?.agents_with_log_today ?? 0} / ${overview?.agents_total ?? 8}`
          }
          loading={loading}
        />
        <KpiCell
          label="Recent agent PRs"
          value={loading ? '…' : overview?.prs_recent_count ?? 0}
          loading={loading}
        />
        <KpiCell
          label="Recent agent emails"
          value={loading ? '…' : overview?.emails_recent_count ?? 0}
          loading={loading}
        />
        <KpiCell
          label="state.md last edit"
          value={loading ? '…' : formatLastEdit(overview?.state_md_last_edit ?? null)}
          loading={loading}
        />
      </Box>
    </>
  );
}
